import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { StudioLibrary } from "@/components/apps/StudioLibrary";
import { STUDIO_EXAMPLE_ASKS } from "@/components/apps/studioChatStarters";
import { Toast } from "@/components/ui/Toast";
import { useStudioApps } from "@/hooks/useStudioApps";
import { useThreads } from "@/hooks/useThreads";

export const Route = createFileRoute("/_authenticated/studio/")({
  component: StudioLibraryRoute,
});

/**
 * The Studio library route — thin by design: data (`useStudioApps`) and
 * navigation live here; the grid + cards are pure (`StudioLibrary`). See
 * studio spec §4.4.
 *
 * "New app" creates a fresh thread STAMPED `metadata.source: "studio"`
 * (hidden from the chat sidebar) and lands on the gestation route
 * `/studio/new/$threadId`, which runs the turn-end scan (§3.4) and
 * redirects to `/studio/$appDir` once the app's first files land.
 */
function StudioLibraryRoute() {
  const { apps, isLoading, error } = useStudioApps();
  const { createThread } = useThreads();
  const navigate = useNavigate();
  const [createFailed, setCreateFailed] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // The list load can fail (action-backed, no reactive retry — spec §1.2):
  // surface the copy, which asks the user to refresh.
  const toastMessage = createFailed
    ? "Couldn't start a new app build — try again."
    : error;

  return (
    <>
      <StudioLibrary
        apps={apps}
        isLoading={isLoading}
        exampleAsks={STUDIO_EXAMPLE_ASKS}
        onOpenApp={(appDir) => {
          void navigate({ to: "/studio/$appDir", params: { appDir } });
        }}
        onNewApp={() => {
          void createThread({
            title: "New app",
            metadata: { source: "studio" },
          })
            .then((threadId) => {
              void navigate({
                to: "/studio/new/$threadId",
                params: { threadId: threadId as string },
                search: {},
              });
            })
            .catch(() => {
              setCreateFailed(true);
              clearTimeout(toastTimer.current);
              toastTimer.current = setTimeout(() => setCreateFailed(false), 4000);
            });
        }}
      />
      <Toast
        isVisible={toastMessage !== null && toastMessage !== ""}
        icon={<AlertTriangle className="h-4 w-4 text-home-terracotta" />}
        message={toastMessage ?? ""}
      />
    </>
  );
}
